import { useCallback, useEffect, useState } from 'react';
import apiService, { ServiceType } from '../services/api';

const coreService = apiService.getService(ServiceType.CORE);

/**
 * Snapshot from GET /pair/allocation/:dataConfigId/balance.
 * See fortuna-by-code-be/src/pair/pair-allocator.service.ts for the canonical shape.
 * All decimal-like fields are returned as strings (Prisma.Decimal serialization) and
 * the caller should parse with Number() / BigDecimal as needed.
 */
export interface PairAllocationBalance {
    dataConfigId: string;
    stockLeg: {
        portfolioId: string;
        externalPortfolioId: string;
        allocatedCash: string;
        usedCash: string;
        idleCash: string;
        holdingsCost: string;
    };
    derivLeg: {
        portfolioId: string;
        externalPortfolioId: string;
        allocatedCash: string;
        /** Real free cash at the broker; '' when the snapshot didn't report it. */
        purchasingPower: string;
        /** min(allocatedCash, purchasingPower) — what this pair may actually plan with. */
        collateral: string;
        requiredMargin: string;
        idleCollateral: string;
        multiplier: number;
        /** Broker's floor — the only rate in play. */
        minMarginRate: number;
        brokerCode: string | null;
        markPrice: number;
        /** Contracts the idle collateral carries AT the floor. */
        contractsAffordable: number;
        /** Rate the collateral works out to for the notional held; 0 when flat. */
        impliedMarginRate: number;
        marginStatus?: string;
        notional: string;
    };
    balance: {
        mode: string;
        maxNotional: string;
        bottleneckLeg: 'STOCK' | 'DERIV';
    };
    warnings: string[];
}

/**
 * Live pair allocation balance. Refreshes on `dataConfigId` change and exposes
 * a manual `refresh()` so the manage page can re-fetch after a deposit/withdraw
 * ack. Doesn't poll on its own — caller decides cadence (typically tied to
 * react-query or visibility events).
 */
export function usePairAllocationBalance(dataConfigId: string | null) {
    const [data, setData] = useState<PairAllocationBalance | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchBalance = useCallback(async () => {
        if (!dataConfigId) {
            setData(null);
            return;
        }
        const token = localStorage.getItem('access_token');
        if (!token) {
            setError('not authenticated');
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const response = await coreService.get<PairAllocationBalance>(
                `/pair/allocation/${dataConfigId}/balance`,
                { headers: { Authorization: `Bearer ${token}` } },
            );
            setData(response.data);
        } catch (e: any) {
            setError(e?.response?.data?.message ?? e?.message ?? 'failed to fetch balance');
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [dataConfigId]);

    useEffect(() => {
        fetchBalance();
    }, [fetchBalance]);

    return { data, loading, error, refresh: fetchBalance };
}
